import { useMemo } from 'react';

export interface SerializedIntakeWriteQueue {
  enqueue<T>(intakeItemId: string, write: () => Promise<T>): Promise<T>;
}

/**
 * Serialize writes to the same Intake Item while allowing unrelated items to
 * save concurrently. A rejected write remains rejected for its caller, but is
 * normalized before it becomes the next write's dependency so retries and
 * subsequent edits are never poisoned by an earlier failure.
 */
export function createSerializedIntakeWriteQueue(): SerializedIntakeWriteQueue {
  const tails = new Map<string, Promise<void>>();

  return {
    enqueue<T>(intakeItemId: string, write: () => Promise<T>): Promise<T> {
      const previous = tails.get(intakeItemId) ?? Promise.resolve();
      const result = previous.then(write);
      const normalizedTail = result.then(
        () => undefined,
        () => undefined,
      );

      tails.set(intakeItemId, normalizedTail);
      void normalizedTail.then(() => {
        if (tails.get(intakeItemId) === normalizedTail) {
          tails.delete(intakeItemId);
        }
      });

      return result;
    },
  };
}

export function useSerializedIntakeWrites(): SerializedIntakeWriteQueue {
  return useMemo(() => createSerializedIntakeWriteQueue(), []);
}
